import { useEffect, useRef, useState, type MouseEvent } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight, ChevronLeft, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import {
  GenieQuickTestModal,
  PronunciationModal,
  QRCodeModal,
  WebEmbedModal,
  webEmbedTabFor,
} from "@/components/agent";
import { ControlsBar, GenieDetailHeader } from "@/components/agent/details";
import GenieShareDrawer from "@/components/agent/drawers/GenieShareDrawer";
import { GenieLoader } from "@/components/ui/GenieLoader";
import { GenieSetupImmersive } from "@/components/agent/setup/GenieSetupImmersive";
import { BrandingModal } from "@/components/branding";
import { ListenerPhoneDrawer, PhoneNumberModal } from "@/components/phone";
import { useAgent } from "@/hooks/agent";
import { useDeleteAgent } from "@/hooks/agent/useAgents";
import { buildGenieSetupState } from "@/hooks/agent/useGenieSetupState";
import { useRole } from "@/hooks/auth/useRole";
import { supabase } from "@/integrations/supabase/client";
import { DocumentService } from "@/services/api/DocumentService";
import { lsGet, lsSet, readGenieSetupOverrides } from "@/utils/agent/genieSetupStorage";
import { Agent } from "@/types/agent/agent";
import { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";
import { DEFAULT_BRANDING } from "@/types/ui/branding";
import { getReadableGenieBrandTheme } from "@/utils/branding/genieBrandTheme";
import { GenieDetailModal } from "./components/genieDetailSections";
import { LISTENER_SECTION_FALLBACKS, ListenerGenieSections } from "./components/ListenerGenieSections";
import { VOICE_SECTION_FALLBACKS, VoiceGenieSections } from "./components/VoiceGenieSections";

const SETUP_GUIDANCE_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
const ACTIVATE_FAB_DISMISSED_KEY = "hg:activateFabDismissed";

const isWithinSetupGuidanceWindow = (createdAt: string | null): boolean => {
  if (!createdAt) return true;

  const createdAtMs = new Date(createdAt).getTime();
  return Number.isNaN(createdAtMs) || Date.now() - createdAtMs < SETUP_GUIDANCE_WINDOW_MS;
};

const GenieDetailPage = () => {
  const { genieId } = useParams<{ genieId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { data: genie, isLoading } = useAgent(genieId!, { initialData: (state as { genie?: Agent })?.genie });
  const isTeamGenie = user?.id && genie?.user_id !== user.id;
  const { isInternalAdmin } = useRole();

  useEffect(() => {
    if (!genie?.elevenlabs_id || !genieId) return;

    const refresh = () => queryClient.invalidateQueries({ queryKey: ["agent", genieId] });

    const updateChannel = supabase
      .channel("agent-updates")
      .on("broadcast", { event: "knowledge_base_updated" }, ({ payload }: { payload: { agentId?: string } }) => {
        if (payload.agentId === genieId) {
          queryClient.invalidateQueries({ queryKey: ["documents-infinite"] });
          queryClient.invalidateQueries({ queryKey: ["knowledge-base-docs"] });
          queryClient.invalidateQueries({ queryKey: ["documents"] });
          refresh();
        }
      })
      .subscribe();

    return () => {
      updateChannel.unsubscribe();
    };
  }, [genieId, genie?.elevenlabs_id, queryClient]);

  const [activeModal, setActiveModal] = useState<GenieDetailModal | null>(null);
  useEffect(() => {
    const open = searchParams.get("open");
    if (open === "phone") setActiveModal("phone");
  }, [searchParams]);

  const [viewMode, setViewMode] = useState<"list" | "grid">("list");
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const effectiveViewMode = isMobile ? "list" : viewMode;

  const isListener = genie?.type === "listener";

  useEffect(() => {
    const section = searchParams.get("section");
    if (!section) return;
    const fallbacks = isListener ? LISTENER_SECTION_FALLBACKS : VOICE_SECTION_FALLBACKS;
    const el = document.getElementById(section) ?? document.getElementById(fallbacks[section] ?? "");
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [searchParams, genie, effectiveViewMode]);

  // Once the hero Activation Pack section scrolls out of view, keep a floating
  // reminder on screen — activation is the single most important next step.
  // A callback ref (rather than a getElementById + dep-array guess) ties the
  // observer directly to the DOM node actually mounting, so it can't miss it.
  const [distributionEl, setDistributionEl] = useState<HTMLDivElement | null>(null);
  const [showActivateFab, setShowActivateFab] = useState(false);
  useEffect(() => {
    if (!distributionEl) return;
    const root = document.getElementById("main-scroll-container");

    const observer = new IntersectionObserver(([entry]) => setShowActivateFab(!entry.isIntersecting), {
      root,
      threshold: 0,
    });
    observer.observe(distributionEl);
    return () => observer.disconnect();
  }, [distributionEl]);

  // Dismissing the reminder is remembered per genie — once someone has said
  // "not now" for this genie we stop nagging on every future visit.
  const [activateFabDismissed, setActivateFabDismissed] = useState(false);
  useEffect(() => {
    if (!genieId) return;
    setActivateFabDismissed(localStorage.getItem(`${ACTIVATE_FAB_DISMISSED_KEY}:${genieId}`) === "1");
  }, [genieId]);

  const dismissActivateFab = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setActivateFabDismissed(true);
    if (genieId) localStorage.setItem(`${ACTIVATE_FAB_DISMISSED_KEY}:${genieId}`, "1");
  };

  const [isDeleting, setIsDeleting] = useState(false);
  const deleteAgentMutation = useDeleteAgent();

  const [isReassigning, setIsReassigning] = useState(false);
  const handleReassignDocuments = async () => {
    if (!genie?.user_id) return;
    const mismatchedDocIds = ((genie.knowledge_base ?? []) as DocumentWithMetadata[])
      .filter((doc) => doc.user_id && doc.user_id !== genie.user_id)
      .map((doc) => doc.id);
    if (mismatchedDocIds.length === 0) return;

    setIsReassigning(true);
    try {
      await DocumentService.bulkUpdateDocuments(mismatchedDocIds, { user_id: genie.user_id });
      await queryClient.invalidateQueries({ queryKey: ["agent", genieId] });
      toast.success("Documents reassigned to the genie's user");
    } catch {
      toast.error("Failed to reassign documents");
    } finally {
      setIsReassigning(false);
    }
  };

  // The checklist is a setup surface: once every step is done or skipped it has
  // served its purpose, so a completed genie lands on the full workspace instead.
  // Completion is sampled once per genie — the first render with data — so
  // finishing the last step while inside the checklist doesn't yank the owner
  // out mid-session; the workspace takes over from the next visit.
  const setupCompleteRef = useRef<{ genieId: string; complete: boolean } | null>(null);
  if (genie && setupCompleteRef.current?.genieId !== genie.id) {
    setupCompleteRef.current = {
      genieId: genie.id,
      complete: buildGenieSetupState(genie, readGenieSetupOverrides(genie.id)).isComplete,
    };
  }
  const setupComplete = setupCompleteRef.current?.complete ?? false;

  // New genies get focused setup guidance. Established genies open straight into
  // their workspace, where activation and all ongoing features are more useful.
  // A completed genie also ignores a persisted "setup" preference — it predates
  // completion — but an explicit click this session still opens the checklist.
  // A link to a section or panel always lands on the workspace, where those live.
  const deepLinked = searchParams.has("section") || searchParams.has("open");
  const setupModeKey = `hg-genie-mode-${genieId}`;
  const [savedSetupMode] = useState<"setup" | "full" | null>(() => {
    const savedMode = lsGet(setupModeKey);
    return savedMode === "setup" || savedMode === "full" ? savedMode : null;
  });
  const [sessionSetupMode, setSessionSetupMode] = useState<"setup" | "full" | null>(null);
  const setupMode =
    sessionSetupMode ??
    (setupComplete || deepLinked
      ? "full"
      : savedSetupMode ?? (isWithinSetupGuidanceWindow(genie?.created_at ?? null) ? "setup" : "full"));
  const goToMode = (mode: "setup" | "full") => {
    setSessionSetupMode(mode);
    lsSet(setupModeKey, mode);
  };

  const handleDelete = async () => {
    if (!genie) return;
    setIsDeleting(true);
    try {
      await deleteAgentMutation.mutateAsync({ agent: genie });
      navigate("/genie-hub/my-genies");
    } catch {
      toast.error("Failed to delete genie");
    } finally {
      setIsDeleting(false);
      setActiveModal(null);
    }
  };

  if (isLoading) {
    return <GenieLoader size="fullscreen" loadingText="Loading genie details…" />;
  }

  if (!genie) {
    return (
      <div className="min-h-screen bg-[#F9FAFC] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Genie Not Found</h2>
          <p className="text-gray-600 mb-4">
            The genie you're looking for doesn't exist or you don't have access to it.
          </p>
          <button
            onClick={() => navigate("/genie-hub/my-genies")}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to My Genies
          </button>
        </div>
      </div>
    );
  }

  const brand = getReadableGenieBrandTheme(genie).primary;

  const inSetupCanvas = !isTeamGenie && setupMode === "setup";

  const hasOwnershipMismatch =
    isInternalAdmin() &&
    !!genie.user_id &&
    ((genie.knowledge_base ?? []) as DocumentWithMetadata[]).some(
      (doc) => doc.user_id && doc.user_id !== genie.user_id,
    );
  const knowledgeNotice = hasOwnershipMismatch && (
    <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
      <div className="flex items-start gap-2">
        <span className="mt-0.5 shrink-0">⚠️</span>
        <span>Some knowledge base documents attached to this genie are not owned by the genie's assigned user.</span>
      </div>
      <button
        type="button"
        onClick={handleReassignDocuments}
        disabled={isReassigning}
        className="shrink-0 px-3 py-1.5 text-sm font-medium text-amber-900 bg-amber-100 border border-amber-300 rounded-lg hover:bg-amber-200 transition-colors disabled:opacity-50"
      >
        {isReassigning ? "Reassigning…" : "Reassign"}
      </button>
    </div>
  );
  const hasBranding: boolean = Boolean(
    genie.branding &&
    (genie.branding.primaryColor !== DEFAULT_BRANDING.primaryColor ||
      genie.branding.secondaryColor ||
      genie.branding.customAvatarUrl ||
      genie.branding.gradientEnabled),
  );

  const handleShare = () => {
    if (!(genie.url_name ?? genie.page?.url_name)) {
      toast.error("Unable to generate share link");
      return;
    }

    setActiveModal("shareDrawer");
  };

  if (inSetupCanvas) {
    return (
      <GenieSetupImmersive genie={genie} onViewAll={() => goToMode("full")} />
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFC]">
      <div className="container mx-auto px-4 py-6 md:py-8 max-w-[1400px]">
        <ControlsBar
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          showDelete={!isTeamGenie || isInternalAdmin()}
          onDelete={() => setActiveModal("deleteConfirm")}
        />

        <GenieDetailHeader
          genie={genie}
          brand={brand}
          hasBranding={hasBranding}
          isTeamGenie={!!isTeamGenie}
          onQuickTest={isListener ? undefined : () => setActiveModal("quickTest")}
          onShare={handleShare}
          onSetUpMailbox={() =>
            document.getElementById("section-mailbox")?.scrollIntoView({ behavior: "smooth", block: "start" })
          }
        />

        {!isTeamGenie && (
          <button
            type="button"
            onClick={() => goToMode("setup")}
            className="mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-genie-primary hover:underline"
          >
            <ChevronLeft className="w-4 h-4" />
            Explore setup checklist
          </button>
        )}

        {isListener ? (
          <ListenerGenieSections
            genie={genie}
            brand={brand}
            layout={effectiveViewMode}
            isAdmin={isInternalAdmin()}
            knowledgeNotice={knowledgeNotice}
            onOpenModal={setActiveModal}
            distributionRef={setDistributionEl}
          />
        ) : (
          <VoiceGenieSections
            genie={genie}
            brand={brand}
            layout={effectiveViewMode}
            isAdmin={isInternalAdmin()}
            knowledgeNotice={knowledgeNotice}
            onOpenModal={setActiveModal}
            distributionRef={setDistributionEl}
          />
        )}
      </div>

      {showActivateFab && !activateFabDismissed && activeModal === null && (
        <Link
          to={`/genie-hub/activation/genie/${genie.id}`}
          className="group fixed bottom-6 right-6 z-40 flex flex-col gap-5 w-72 sm:w-80 px-6 py-7 rounded-3xl text-white shadow-2xl transition-all animate-in fade-in slide-in-from-bottom-4 hover:-translate-y-1"
          style={{
            background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)",
            boxShadow: "0 32px 64px -16px rgba(78,156,255,0.6)",
          }}
        >
          <button
            type="button"
            onClick={dismissActivateFab}
            aria-label="Dismiss activation reminder"
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 ring-1 ring-white/30 flex items-center justify-center text-white/90 hover:bg-white/30 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="pr-8">
            <span className="text-xs font-bold tracking-[0.16em] uppercase text-white/80 block">
              Nothing matters more right now
            </span>
            <div className="text-2xl font-bold leading-tight mt-1.5">Activate your Genie</div>
            <p className="text-sm text-white/85 leading-snug mt-2">
              Get your ready-to-ship toolkit — QR codes, link, posters and more.
            </p>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[15px] font-bold">Open activation pack</span>
            <span className="w-11 h-11 rounded-full bg-white/20 ring-1 ring-white/30 flex items-center justify-center flex-shrink-0 transition-transform group-hover:translate-x-1">
              <ArrowRight className="w-5 h-5" />
            </span>
          </div>
        </Link>
      )}

      {activeModal === "deleteConfirm" && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold mb-2">Delete Genie</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <strong>{genie.name}</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setActiveModal(null)}
                className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      <PronunciationModal genie={genie} isOpen={activeModal === "pronunciation"} onClose={() => setActiveModal(null)} />

      <BrandingModal
        genie={genie}
        isOpen={activeModal === "branding"}
        onClose={() => setActiveModal(null)}
        onSwitchModal={setActiveModal}
      />

      <GenieQuickTestModal genie={genie} isOpen={activeModal === "quickTest"} onClose={() => setActiveModal(null)} />

      <GenieShareDrawer
        genie={genie}
        isOpen={activeModal === "shareDrawer"}
        onClose={() => setActiveModal(null)}
        onOpenPhoneModal={() => setActiveModal("phone")}
      />

      {isListener ? (
        <ListenerPhoneDrawer genie={genie} isOpen={activeModal === "phone"} onClose={() => setActiveModal(null)} />
      ) : (
        <PhoneNumberModal genie={genie} isOpen={activeModal === "phone"} onClose={() => setActiveModal(null)} />
      )}
      <WebEmbedModal
        genie={genie}
        isOpen={webEmbedTabFor(activeModal) !== null}
        initialTab={webEmbedTabFor(activeModal) ?? undefined}
        onClose={() => setActiveModal(null)}
      />
      <QRCodeModal genie={genie} isOpen={activeModal === "qr"} onClose={() => setActiveModal(null)} />
    </div>
  );
};

export default GenieDetailPage;
